/**
 * Hook for loading and managing JTL channel descriptions.
 *
 * Fetches live descriptions from JTL, groups by SalesChannelId,
 * and provides a push mutation for saving changes back to JTL.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getProviderDescriptions,
  pushDescriptionToJTL,
  type JTLDescription,
  type PushDescriptionRequest,
} from '@/lib/api/descriptions'

export type GroupedDescriptions = Record<string, JTLDescription[]>

function groupByChannel(descriptions: JTLDescription[]): GroupedDescriptions {
  const grouped: GroupedDescriptions = {}
  for (const desc of descriptions) {
    const key = desc.SalesChannelId
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(desc)
  }
  return grouped
}

function extractLanguages(descriptions: JTLDescription[]): string[] {
  const langs = new Set(descriptions.map((d) => d.LanguageIso))
  return Array.from(langs).sort()
}

function getMasterData(grouped: GroupedDescriptions, lang: string): JTLDescription | undefined {
  const masterDescs = grouped['1-1-0'] ?? []
  return masterDescs.find((d) => d.LanguageIso === lang)
}

function getChannelData(
  grouped: GroupedDescriptions, channelId: string, lang: string,
): JTLDescription | undefined {
  const descs = grouped[channelId] ?? []
  return descs.find((d) => d.LanguageIso === lang)
}

function getChannelIds(grouped: GroupedDescriptions): string[] {
  return Object.keys(grouped).sort((a, b) => {
    if (a === '1-1-0') return -1
    if (b === '1-1-0') return 1
    return a.localeCompare(b)
  })
}

export function useChannelDescriptions(productId: string) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['channel-descriptions', productId],
    queryFn: () => getProviderDescriptions(productId),
    enabled: !!productId,
    staleTime: 30_000,
  })

  const descriptions = query.data?.data ?? []
  const grouped = groupByChannel(descriptions)
  const languages = extractLanguages(descriptions)
  const channelNames = query.data?.channel_names ?? {}

  const pushMutation = useMutation({
    mutationFn: (data: PushDescriptionRequest) =>
      pushDescriptionToJTL(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['channel-descriptions', productId],
      })
      // Content blocks cache invalidieren damit Übersicht aktuell ist
      queryClient.invalidateQueries({
        queryKey: ['content-blocks-channels', productId],
      })
      queryClient.invalidateQueries({
        queryKey: ['content-blocks', productId],
      })
    },
  })

  const channelIds = getChannelIds(grouped)

  const getMaster = (lang: string) => getMasterData(grouped, lang)
  const getChannel = (channelId: string, lang: string) => getChannelData(grouped, channelId, lang)

  return { query, grouped, languages, channelIds, channelNames, pushMutation, getMaster, getChannel }
}
